import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Camera, Users2, LogIn, LogOut } from 'lucide-react';

export default function PhotoMonitoringPage() {
  const { user } = useAuth();
  const today = new Date().toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [branches, setBranches] = useState([]);
  const [branchFilter, setBranchFilter] = useState('all');
  const [selfies, setSelfies] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [viewer, setViewer] = useState({ open: false, src: '', title: '' });

  const isAllowed = ['super_admin', 'management', 'branch_manager'].includes(user?.system_role);
  const isCorporate = ['super_admin', 'management'].includes(user?.system_role);

  const fetchData = useCallback(async () => {
    try {
      const params = { start_date: startDate, end_date: endDate };
      if (isCorporate && branchFilter && branchFilter !== 'all') params.branch_id = branchFilter;
      const [selfiesRes, meetRes] = await Promise.all([
        api.get('/attendance/selfies', { params }),
        api.get('/meeting-photos', { params: { month: startDate.slice(0, 7) } }),
      ]);
      setSelfies(selfiesRes.data.rows || []);
      // Filter meetings to the selected range (inclusive)
      const dayMeetings = meetRes.data.filter(m => (m.date || '') >= startDate && (m.date || '') <= endDate);
      setMeetings(dayMeetings);
    } catch (err) { console.error(err); }
  }, [startDate, endDate, branchFilter, isCorporate]);

  useEffect(() => { if (isAllowed) fetchData(); }, [isAllowed, fetchData]);
  useEffect(() => {
    if (isCorporate) api.get('/branches').then(res => setBranches(res.data || [])).catch(console.error);
  }, [isCorporate]);

  if (!isAllowed) {
    return <div className="p-6 text-neutral-800" data-testid="photo-monitoring-forbidden">Access denied.</div>;
  }

  const openPhotoById = async (row, which) => {
    try {
      const { data } = await api.get(`/attendance/selfies/${row.attendance_id}`, { params: { which } });
      if (!data.photo_base64) return;
      const full = data.photo_base64.startsWith('data:') ? data.photo_base64 : `data:image/jpeg;base64,${data.photo_base64}`;
      setViewer({ open: true, src: full, title: `${row.employee_name} · ${which === 'in' ? 'Punch-in' : 'Punch-out'} ${row.date}` });
    } catch (err) { console.error(err); }
  };

  const openMeetingPhoto = async (m) => {
    try {
      const { data } = await api.get(`/meeting-photos/${m.id}`);
      const full = (data.photo_base64 || '').startsWith('data:') ? data.photo_base64 : `data:image/jpeg;base64,${data.photo_base64}`;
      setViewer({ open: true, src: full, title: `${m.branch_name || 'Meeting'} · ${m.date}` });
    } catch (err) { console.error(err); }
  };

  // Group by branch for the branch-wise view
  const byBranch = selfies.reduce((acc, r) => {
    const bn = r.branch_name || 'Corporate';
    (acc[bn] = acc[bn] || []).push(r);
    return acc;
  }, {});

  return (
    <div className="space-y-6 animate-fade-in" data-testid="photo-monitoring-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading font-black text-neutral-950 text-2xl sm:text-3xl tracking-tight">Photo Monitoring</h1>
          <p className="text-neutral-700 text-sm mt-1">Branch-wise verification of daily punch-in / punch-out selfies and meeting photos.</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          {isCorporate && (
            <div>
              <Label className="text-xs text-neutral-500">Branch</Label>
              <Select value={branchFilter} onValueChange={setBranchFilter}>
                <SelectTrigger className="w-48 rounded-sm" data-testid="photo-branch-filter"><SelectValue placeholder="All Branches" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Branches</SelectItem>
                  {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs text-neutral-500">From</Label>
            <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
              className="w-auto rounded-sm" data-testid="photo-start-date" />
          </div>
          <div>
            <Label className="text-xs text-neutral-500">To</Label>
            <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
              className="w-auto rounded-sm" data-testid="photo-end-date" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Punch-in Selfies', value: selfies.filter(r => r.in_time).length, icon: LogIn, color: 'bg-emerald-50 text-emerald-700 border-emerald-100' },
          { label: 'Punch-out Selfies', value: selfies.filter(r => r.out_time).length, icon: LogOut, color: 'bg-blue-50 text-blue-700 border-blue-100' },
          { label: 'Meeting Photos', value: meetings.length, icon: Users2, color: 'bg-indigo-50 text-indigo-700 border-indigo-100' },
          { label: 'Branches', value: Object.keys(byBranch).length, icon: Camera, color: 'bg-amber-50 text-amber-700 border-amber-100' },
        ].map((s) => (
          <div key={s.label} className={`border rounded-md p-4 ${s.color}`}>
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-bold uppercase tracking-wider opacity-70">{s.label}</p>
              <s.icon className="h-4 w-4 opacity-60" />
            </div>
            <p className="text-3xl font-bold tracking-tight mt-1" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g,'-')}`}>{s.value}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="branches">
        <TabsList>
          <TabsTrigger value="branches" data-testid="tab-branch-selfies">By Branch</TabsTrigger>
          <TabsTrigger value="entry" data-testid="tab-entry-photos">Punch-in</TabsTrigger>
          <TabsTrigger value="exit" data-testid="tab-exit-photos">Punch-out</TabsTrigger>
          <TabsTrigger value="meetings" data-testid="tab-meeting-photos">Meetings</TabsTrigger>
        </TabsList>

        <TabsContent value="branches" className="mt-4 space-y-6">
          {Object.keys(byBranch).length === 0 && (
            <Card className="border border-neutral-200 rounded-md">
              <CardContent className="py-14 text-center text-neutral-700">
                <Camera className="h-8 w-8 mx-auto mb-2 text-neutral-300" />
                <p className="text-sm">No attendance selfies for this branch/date range.</p>
              </CardContent>
            </Card>
          )}
          {Object.entries(byBranch).map(([bn, rows]) => (
            <div key={bn} data-testid={`branch-group-${bn}`}>
              <h2 className="text-sm font-semibold text-neutral-800 mb-2">{bn} · {rows.length} punch-ins</h2>
              <SelfieGrid items={rows} which="in" onOpen={openPhotoById} />
            </div>
          ))}
        </TabsContent>

        <TabsContent value="entry" className="mt-4">
          <SelfieGrid items={selfies.filter(r => r.in_time)} which="in" onOpen={openPhotoById} />
        </TabsContent>

        <TabsContent value="exit" className="mt-4">
          <SelfieGrid items={selfies.filter(r => r.out_time)} which="out" onOpen={openPhotoById} />
        </TabsContent>

        <TabsContent value="meetings" className="mt-4">
          <MeetingGrid items={meetings} onOpen={openMeetingPhoto} />
        </TabsContent>
      </Tabs>

      <Dialog open={viewer.open} onOpenChange={(o) => !o && setViewer({ open: false, src: '', title: '' })}>
        <DialogContent className="max-w-2xl" data-testid="photo-viewer-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">{viewer.title}</DialogTitle>
            <DialogDescription>Full-size verification view</DialogDescription>
          </DialogHeader>
          {viewer.src && <img src={viewer.src} alt={viewer.title} className="w-full max-h-[65dvh] object-contain rounded-md border border-neutral-200" data-testid="photo-viewer-image" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SelfieGrid({ items, which, onOpen }) {
  if (items.length === 0) {
    return (
      <Card className="border border-neutral-200 rounded-md">
        <CardContent className="py-8 text-center text-neutral-500 text-sm">No records.</CardContent>
      </Card>
    );
  }
  return (
    <div className="photo-monitoring-grid grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="selfie-grid">
      {items.map(r => (
        <button key={r.attendance_id} onClick={() => onOpen(r, which)}
          className="photo-monitoring-record group relative overflow-hidden rounded-md border border-neutral-200 hover:border-red-400 transition-colors text-left"
          data-testid={`selfie-card-${r.attendance_id}`}>
          <div className="aspect-square bg-neutral-100 flex items-center justify-center">
            <Camera className="h-10 w-10 text-neutral-300 group-hover:text-red-500 transition-colors" />
          </div>
          <div className="p-2">
            <p className="text-sm font-semibold text-neutral-900 whitespace-normal break-words" data-testid={`selfie-name-${r.attendance_id}`}>{r.employee_name}</p>
            <p className="text-xs text-neutral-600 whitespace-normal break-words" data-testid={`selfie-date-${r.attendance_id}`}>{r.employee_code} · {r.date}</p>
            <p className="text-xs text-neutral-600" data-testid={`selfie-punch-times-${r.attendance_id}`}>In: {r.in_time || '—'} · Out: {r.out_time || '—'}</p>
          </div>
        </button>
      ))}
    </div>
  );
}

function MeetingGrid({ items, onOpen }) {
  if (items.length === 0) {
    return (
      <Card className="border border-neutral-200 rounded-md">
        <CardContent className="py-8 text-center text-neutral-500 text-sm">No meeting photos.</CardContent>
      </Card>
    );
  }
  return (
    <div className="photo-monitoring-grid grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="meeting-photo-grid">
      {items.map(m => (
        <button key={m.id} onClick={() => onOpen(m)}
          data-testid={`meeting-photo-card-${m.id}`}
          className="photo-monitoring-record group relative overflow-hidden rounded-md border border-neutral-200 hover:border-red-400 transition-colors text-left">
          <div className="aspect-square bg-neutral-100 flex items-center justify-center">
            <Users2 className="h-10 w-10 text-neutral-300" />
          </div>
          <div className="p-2">
            <p className="text-sm font-semibold text-neutral-900 whitespace-normal break-words" data-testid={`meeting-photo-branch-${m.id}`}>{m.branch_name || 'Meeting'}</p>
            <p className="text-xs text-neutral-600 whitespace-normal break-words" data-testid={`meeting-photo-meta-${m.id}`}>{m.date} · by {m.uploaded_by_name}</p>
          </div>
        </button>
      ))}
    </div>
  );
}
